import React, { useState, useRef, useCallback, useEffect } from 'react';
import cruzTemplaria from '@/assets/cruz-templaria.svg';
import videoPoster from '@/assets/video-poster.jpg';

interface VimeoPlayerProps {
  videoId: string | number;
}

const formatTime = (s: number) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

export const VimeoPlayer: React.FC<VimeoPlayerProps> = ({ videoId }) => {
  const [phase, setPhase] = useState<'idle' | 'playing' | 'paused'>('idle');
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout>>();
  const progressRef = useRef<HTMLDivElement>(null);
  const iframeReady = useRef(false);

  const postMsg = useCallback((method: string, value?: any) => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    const msg: any = { method };
    if (value !== undefined) msg.value = value;
    win.postMessage(JSON.stringify(msg), '*');
  }, []);

  // Listen for messages from Vimeo iframe
  useEffect(() => {
    if (phase === 'idle') return;

    const handler = (e: MessageEvent) => {
      if (!e.origin.includes('vimeo.com')) return;
      let data: any;
      try {
        data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
      } catch {
        return;
      }

      if (data.event === 'ready') {
        iframeReady.current = true;
        // Subscribe to events
        postMsg('addEventListener', 'playProgress');
        postMsg('addEventListener', 'play');
        postMsg('addEventListener', 'pause');
        postMsg('addEventListener', 'finish');
        postMsg('getDuration');
        postMsg('play');
      }

      if (data.method === 'getDuration') {
        setDuration(data.value || 0);
      }

      if (data.event === 'playProgress' && data.data) {
        setCurrentTime(data.data.seconds || 0);
        setDuration(data.data.duration || duration);
        setProgress(data.data.percent || 0);
      }

      if (data.event === 'play') {
        setPhase('playing');
      }
      if (data.event === 'pause') {
        setPhase('paused');
      }
      if (data.event === 'finish') {
        setPhase('paused');
        setProgress(1);
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [phase, postMsg, duration]);

  const handlePlay = useCallback(() => {
    if (phase === 'idle') {
      setPhase('playing');
    } else {
      postMsg('play');
      setPhase('playing');
    }
  }, [phase, postMsg]);

  const handlePause = useCallback(() => {
    postMsg('pause');
    setPhase('paused');
  }, [postMsg]);

  const togglePlayPause = useCallback(() => {
    if (phase === 'playing') handlePause();
    else handlePlay();
  }, [phase, handlePlay, handlePause]);

  const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressRef.current || !duration) return;
    const rect = progressRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const seekTo = pct * duration;
    postMsg('seekTo', seekTo);
    setProgress(pct);
    setCurrentTime(seekTo);
  }, [duration, postMsg]);

  const handleFullscreen = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      el.requestFullscreen?.();
    }
  }, []);

  // Auto-hide controls
  const resetHideTimer = useCallback(() => {
    setShowControls(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    if (phase === 'playing') {
      hideTimer.current = setTimeout(() => setShowControls(false), 2500);
    }
  }, [phase]);

  useEffect(() => {
    if (phase === 'playing') {
      hideTimer.current = setTimeout(() => setShowControls(false), 2500);
    } else {
      setShowControls(true);
    }
    return () => { if (hideTimer.current) clearTimeout(hideTimer.current); };
  }, [phase]);

  const iframeSrc = `https://player.vimeo.com/video/${videoId}?api=1&autoplay=0&title=0&byline=0&portrait=0&badge=0&controls=0&autopause=0&dnt=1&player_id=vp-${videoId}&app_id=58479`;

  return (
    <div
      className="vp-root"
      ref={rootRef}
      onMouseMove={phase !== 'idle' ? resetHideTimer : undefined}
    >
      {/* Idle overlay with poster */}
      {phase === 'idle' && (
        <div className="vp-overlay" onClick={handlePlay}>
          <img
            src={videoPoster}
            alt="Capa do vídeo"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: 0,
            }}
          />
          <button className="vp-big-play" aria-label="Reproduzir" style={{ position: 'relative', zIndex: 1 }}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <polygon points="5,3 19,12 5,21" />
            </svg>
          </button>
        </div>
      )}

      {/* Iframe - always mounted after first play, but pointer-events disabled */}
      {phase !== 'idle' && (
        <div className="vp-iframe-wrap">
          <iframe
            ref={iframeRef}
            src={iframeSrc}
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
            referrerPolicy="strict-origin-when-cross-origin"
            title="Vídeo Templário"
            style={{ pointerEvents: 'none' }}
          />
        </div>
      )}

      {/* Click area for play/pause when video is active */}
      {phase !== 'idle' && (
        <div
          className="vp-click-area"
          onClick={togglePlayPause}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 15,
            cursor: 'none',
          }}
        />
      )}

      {/* Pause overlay - big play button when paused */}
      {phase === 'paused' && (
        <div
          className="vp-overlay"
          style={{ pointerEvents: 'none', zIndex: 16 }}
        >
          <button className="vp-big-play" aria-label="Reproduzir" tabIndex={-1}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <polygon points="5,3 19,12 5,21" />
            </svg>
          </button>
        </div>
      )}

      {/* Custom controls */}
      {phase !== 'idle' && (
        <div
          className="vp-controls"
          style={{
            opacity: showControls ? 1 : 0,
            zIndex: 20,
            pointerEvents: showControls ? 'auto' : 'none',
          }}
        >
          {/* Progress bar */}
          <div className="vp-progress-wrap" ref={progressRef} onClick={handleSeek}>
            <div className="vp-progress-bg">
              <div className="vp-progress-fill" style={{ width: `${progress * 100}%` }} />
              <div className="vp-progress-thumb" style={{ left: `${progress * 100}%` }} />
            </div>
          </div>

          {/* Controls row */}
          <div className="vp-controls-row">
            {/* Play/Pause */}
            <button className="vp-btn" onClick={togglePlayPause} aria-label={phase === 'playing' ? 'Pausar' : 'Reproduzir'}>
              {phase === 'playing' ? (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="4" width="4" height="16" rx="1" />
                  <rect x="14" y="4" width="4" height="16" rx="1" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="5,3 19,12 5,21" />
                </svg>
              )}
            </button>

            {/* Time */}
            <span className="vp-time">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>

            {/* Brand */}
            <div className="vp-brand">
              <img src={cruzTemplaria} alt="Cruz Templária" className="vp-cross" />
              <span>Gran Priorato Templário</span>
            </div>

            {/* Fullscreen */}
            <button className="vp-btn" onClick={handleFullscreen} aria-label="Tela cheia">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
